import { useEffect, useRef, useState } from 'react';

import type { ClauseMiningDryRun } from '@clm/shared-types';

import { cancelClauseMiningDryRun, getClauseMiningDryRun } from '../../api/clause-mining-dry-runs';
import { mayAttempt } from '../../auth/permissions';
import { useIdentity } from '../../auth/useIdentity';
import { PIPELINE_FORBIDDEN_COPY } from '../pipeline-status-chips';
import {
  ACTIVE_POLL_MS,
  APPROVE_ROLES,
  STALE_HEARTBEAT_MS,
  dryRunProblemHead,
  isTerminal,
  summariseResult,
  toDryRunProblem,
  type DryRunProblem,
} from './shared';

/**
 * The one `running` dry run — CLM-103 (first slice).
 *
 * Polls `get-clause-mining-dry-run` every 5 s while mounted; the interval is
 * cleared on unmount and as soon as the row turns terminal, at which point
 * `onFinished` is called **once** so the parent reloads the list. A heartbeat
 * older than two minutes renders with the `warn` tone.
 *
 * There is no cost figure here and none is implied: the row this card shows
 * is always a dry run (see `0097`'s `ck_clause_mining_dry_run_dry_run_only`),
 * so "cancel" stops a read, never a spend.
 */
export function ActiveDryRunCard({
  run: initial,
  onFinished,
  now = () => Date.now(),
}: {
  readonly run: ClauseMiningDryRun;
  readonly onFinished: () => void;
  /** Injectable clock for the stale computation. */
  readonly now?: () => number;
}) {
  const { identity } = useIdentity();
  const mayCancel = mayAttempt(identity, APPROVE_ROLES);
  const [run, setRun] = useState<ClauseMiningDryRun>(initial);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<DryRunProblem | null>(null);
  const finishedRef = useRef(false);

  useEffect(() => {
    setRun(initial);
    finishedRef.current = false;
  }, [initial]);

  useEffect(() => {
    if (isTerminal(run)) {
      if (!finishedRef.current) {
        finishedRef.current = true;
        onFinished();
      }
      return;
    }
    const controller = new AbortController();
    const timer = setInterval(() => {
      void getClauseMiningDryRun(run.clause_mining_dry_run_id, controller.signal).then(
        (response) => {
          if (!controller.signal.aborted) setRun(response.run);
        },
        () => undefined,
      );
    }, ACTIVE_POLL_MS);
    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, [run, onFinished]);

  const cancel = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const response = await cancelClauseMiningDryRun(run.clause_mining_dry_run_id);
      setRun(response.run);
    } catch (error) {
      setProblem(toDryRunProblem(error));
    } finally {
      setBusy(false);
    }
  };

  const heartbeatAge = run.heartbeat_utc === null ? null : now() - Date.parse(run.heartbeat_utc);
  const stale = heartbeatAge !== null && heartbeatAge > STALE_HEARTBEAT_MS;

  return (
    <div className="card" role="group" aria-label="Active dry run" data-status={run.status} data-stale={stale ? 'true' : 'false'}>
      <p>
        <strong>{run.stage}</strong> dry run — <strong>{run.status}</strong> — started{' '}
        {run.started_utc === null ? '—' : <time dateTime={run.started_utc}>{run.started_utc}</time>} · approved by{' '}
        {run.approved_by_upn ?? '—'}
      </p>
      <p className="mut" data-tone={stale ? 'warn' : 'plain'}>
        Last heartbeat {run.heartbeat_utc === null ? '—' : <time dateTime={run.heartbeat_utc}>{run.heartbeat_utc}</time>}
        {stale ? ' (stale — the worker has not reported for over two minutes)' : ''}
      </p>
      <p className="mut">{summariseResult(run)}</p>
      <p className="mut">
        Correlation <code>{run.correlation_id}</code>{' '}
        <button
          type="button"
          className="btn qt sm"
          aria-label="Copy correlation id"
          onClick={() => void navigator.clipboard?.writeText(run.correlation_id)}
        >
          Copy
        </button>
      </p>
      {isTerminal(run) ? null : (
        <div className="vacts">
          <button
            type="button"
            className="btn sm"
            disabled={!mayCancel || busy}
            title={mayCancel ? 'Stops the read; nothing was written or spent' : 'Admin role required'}
            aria-label={`Cancel dry run ${run.clause_mining_dry_run_id}`}
            onClick={() => void cancel()}
          >
            Cancel
          </button>
        </div>
      )}
      {problem === null ? null : (
        <div className="banner up" role="status">
          <p className="notice-head">
            <strong>{dryRunProblemHead(problem, PIPELINE_FORBIDDEN_COPY)}</strong>
          </p>
          {problem.field_errors.map((entry) => (
            <p key={entry.path} className="mut">
              {entry.message}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
